import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import {
  IntegrationsDetailActions,
  IntegrationsDetailModuleActions,
  IntegrationsDetailModuleState
} from '~/store/integrations/detail'
import {
  CreateIssueOnIntegrationPayload,
  GetIntegrationInstallationUrlPayload,
  InstallIntegrationPayload,
  IntegrationProviderStatus,
  IntegrationSettingsLevel,
  UpdateIntegrationSettingsPayload
} from '~/types/types'

import IntegrationsListMixin from './integrationsListMixin'
import OwnerDetailMixin from './ownerDetailMixin'

interface IntegrationsDetailStoreComputed {
  integration: () => IntegrationsDetailModuleState['integration']
  installIntegrationPayload: () => IntegrationsDetailModuleState['installIntegrationPayload']
}

interface IntegrationsDetailStoreMethods {
  fetchIntegrationDetails: (
    args: Parameters<
      IntegrationsDetailModuleActions[IntegrationsDetailActions.FETCH_INTEGRATION_DETAILS]
    >[1]
  ) => Promise<void>
  fetchIntegrationLogoUrl: (
    args: Parameters<
      IntegrationsDetailModuleActions[IntegrationsDetailActions.FETCH_INTEGRATION_LOGO_URL]
    >[1]
  ) => Promise<void>
  getIntegrationInstallationUrl: (
    args: Parameters<
      IntegrationsDetailModuleActions[IntegrationsDetailActions.GET_INTEGRATION_INSTALLATION_URL]
    >[1]
  ) => Promise<GetIntegrationInstallationUrlPayload>
  installIntegration: (
    args: Parameters<
      IntegrationsDetailModuleActions[IntegrationsDetailActions.INSTALL_INTEGRATION]
    >[1]
  ) => Promise<InstallIntegrationPayload>
  updateIntegrationSettings: (
    args: Parameters<
      IntegrationsDetailModuleActions[IntegrationsDetailActions.UPDATE_INTEGRATION_SETTINGS]
    >[1]
  ) => Promise<UpdateIntegrationSettingsPayload | null>
  unInstallIntegration: (
    args: Parameters<
      IntegrationsDetailModuleActions[IntegrationsDetailActions.UNINSTALL_INTEGRATION]
    >[1]
  ) => Promise<boolean>
  createIssueOnIntegration: (
    args: Parameters<
      IntegrationsDetailModuleActions[IntegrationsDetailActions.CREATE_ISSUE_ON_INTEGRATION]
    >[1]
  ) => Promise<CreateIssueOnIntegrationPayload>
}

export enum IntegrationShortcodes {
  JIRA = 'jira',
  SLACK = 'slack',
  VANTA = 'vanta'
}

export enum JiraCookieKeys {
  PROVIDER = 'integration-jira-provider',
  OWNER = 'integration-jira-owner',
  OWNER_ID = 'integration-jira-owner-id'
}

export enum SlackCookieKeys {
  PROVIDER = 'integration-slack-provider',
  OWNER = 'integration-slack-owner',
  OWNER_ID = 'integration-slack-owner-id'
}

export enum VantaCookieKeys {
  PROVIDER = 'integration-vanta-provider',
  OWNER = 'integration-vanta-owner',
  OWNER_ID = 'integration-vanta-owner-id'
}

export default defineComponent({
  name: 'IntegrationsDetailMixin',

  mixins: [OwnerDetailMixin, IntegrationsListMixin],

  data() {
    return {
      slackEvents: [
        'New issues introduced in default branch',
        'Issues resolved in default branch',
        'Autofix successful',
        'Repository activation status changed',
        'Repository metric alerts'
      ],
      installingIntegration: false,
      removingIntegration: false,
      installationUrl: '',
      showDeleteConfirmation: false
    }
  },

  computed: {
    ...(mapState('integrations/detail', {
      integration: (state: any) => state.integration,
      installIntegrationPayload: (state: any) => state.installIntegrationPayload
    }) as IntegrationsDetailStoreComputed),

    avatar(): string | null | undefined {
      return this.integration?.enabledBy?.avatar
    },

    email(): string | undefined {
      return this.integration?.enabledBy?.email
    },

    userName(): string | undefined {
      return this.integration?.enabledBy?.fullName || this.email
    },

    showErrorStatus(): boolean {
      return this.integration.status === IntegrationProviderStatus.Error
    },

    integrationLabel(): string {
      const labels: Record<IntegrationShortcodes, string> = {
        [IntegrationShortcodes.JIRA]: 'Jira Cloud',
        [IntegrationShortcodes.SLACK]: 'Slack',
        [IntegrationShortcodes.VANTA]: 'Vanta'
      }

      return labels[this.integration.shortcode as IntegrationShortcodes]
    }
  },

  methods: {
    ...(mapActions('integrations/detail', {
      fetchIntegrationDetails: IntegrationsDetailActions.FETCH_INTEGRATION_DETAILS,
      fetchIntegrationLogoUrl: IntegrationsDetailActions.FETCH_INTEGRATION_LOGO_URL,
      getIntegrationInstallationUrl: IntegrationsDetailActions.GET_INTEGRATION_INSTALLATION_URL,
      installIntegration: IntegrationsDetailActions.INSTALL_INTEGRATION,
      updateIntegrationSettings: IntegrationsDetailActions.UPDATE_INTEGRATION_SETTINGS,
      unInstallIntegration: IntegrationsDetailActions.UNINSTALL_INTEGRATION,
      createIssueOnIntegration: IntegrationsDetailActions.CREATE_ISSUE_ON_INTEGRATION
    }) as IntegrationsDetailStoreMethods),

    async fetchIntegrationData(shortcode: string): Promise<void> {
      const ownerId = await this.getOwnerId(shortcode)

      await this.fetchIntegrationDetails({
        shortcode,
        level: IntegrationSettingsLevel.Owner,
        ownerId
      })

      if (shortcode === IntegrationShortcodes.JIRA) {
        this.$cookies.remove(JiraCookieKeys.OWNER_ID)
      } else if (shortcode === IntegrationShortcodes.SLACK) {
        this.$cookies.remove(SlackCookieKeys.OWNER_ID)
      } else if (shortcode === IntegrationShortcodes.VANTA) {
        this.$cookies.remove(VantaCookieKeys.OWNER_ID)
      } else {
        this.$cookies.remove(`integration-${shortcode}-owner-id`)
      }
    },

    async fetchInstallationUrl(shortcode: string): Promise<string> {
      const ownerId = await this.getOwnerId(shortcode)

      const { url } = await this.getIntegrationInstallationUrl({ shortcode, ownerId })
      this.installationUrl = url ?? ''
      return this.installationUrl
    },

    async getInstallationUrlSetCookiesAndRedirect(shortcode: string): Promise<void> {
      this.installingIntegration = true

      if (!this.installationUrl) {
        await this.fetchInstallationUrl(shortcode)
      }

      const { provider, owner } = this.$route.params

      if (shortcode === IntegrationShortcodes.JIRA) {
        this.$cookies.set(JiraCookieKeys.PROVIDER, provider)
        this.$cookies.set(JiraCookieKeys.OWNER, owner)
        this.$cookies.set(JiraCookieKeys.OWNER_ID, this.owner.id)
      } else if (shortcode === IntegrationShortcodes.SLACK) {
        this.$cookies.set(SlackCookieKeys.PROVIDER, provider)
        this.$cookies.set(SlackCookieKeys.OWNER, owner)
        this.$cookies.set(SlackCookieKeys.OWNER_ID, this.owner.id)
      } else if (shortcode === IntegrationShortcodes.VANTA) {
        this.$cookies.set(VantaCookieKeys.PROVIDER, provider)
        this.$cookies.set(VantaCookieKeys.OWNER, owner)
        this.$cookies.set(VantaCookieKeys.OWNER_ID, this.owner.id)
      } else {
        this.$cookies.set(`integration-${shortcode}-provider`, provider)
        this.$cookies.set(`integration-${shortcode}-owner`, owner)
        this.$cookies.set(`integration-${shortcode}-owner-id`, this.owner.id)
      }

      if (this.installationUrl) {
        window.location.href = this.installationUrl
      } else {
        this.installingIntegration = false
      }
    },

    async getOwnerId(shortcode: string): Promise<string> {
      let ownerIdFromCookies = ''

      if (shortcode === IntegrationShortcodes.JIRA) {
        ownerIdFromCookies = this.$cookies.get(JiraCookieKeys.OWNER_ID)
      } else if (shortcode === IntegrationShortcodes.SLACK) {
        ownerIdFromCookies = this.$cookies.get(SlackCookieKeys.OWNER_ID)
      } else if (shortcode === IntegrationShortcodes.VANTA) {
        ownerIdFromCookies = this.$cookies.get(VantaCookieKeys.OWNER_ID)
      } else {
        ownerIdFromCookies = this.$cookies.get(`integration-${shortcode}-owner-id`)
      }

      if (!ownerIdFromCookies && !this.owner.id) {
        const { provider, owner: login } = this.$route.params
        await this.fetchOwnerID({ login, provider })
      }

      return ownerIdFromCookies || this.owner.id
    },

    async uninstallIntegrationWrapper(shortcode: string): Promise<boolean> {
      this.removingIntegration = true
      try {
        const response = await this.unInstallIntegration({ shortcode, ownerId: this.owner.id })

        if (response) {
          await this.fetchIntegrations({
            level: IntegrationSettingsLevel.Owner,
            ownerId: this.owner.id,
            refetch: true
          })
        }

        return response
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'There was an error uninstalling the integration. Please contact support.'
        )
        this.removingIntegration = false
        this.showDeleteConfirmation = false
      } finally {
        this.removingIntegration = false
        this.showDeleteConfirmation = false
      }

      return false
    }
  }
})
